/**
 * Global auth store.
 *
 * Design decisions based on confirmed backend facts:
 * 1. POST /v1/auth/login returns Session with User.Role.
 * 2. GET /v1/users/me does NOT return role.
 * 3. Therefore role is persisted from login and never overwritten by /me.
 * 4. On app startup, if token exists, /me is called to refresh profile;
 *    if 401, logout. Role always comes from persisted value.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User, SessionDTO, UserMeDTO } from '@aiptyg/types';

const STORAGE_KEY = 'aiptyg_admin_auth';

interface AuthState {
  accessToken: string | null;
  expiresAt: string | null;
  user: User | null;

  setSession: (dto: SessionDTO) => void;
  updateProfile: (dto: UserMeDTO) => void;
  logout: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      accessToken: null,
      expiresAt: null,
      user: null,

      setSession: (dto: SessionDTO) =>
        set({
          accessToken: dto.AccessToken,
          expiresAt: dto.ExpiresAt,
          user: {
            id: dto.User.ID,
            email: dto.User.Email,
            nickname: dto.User.Nickname,
            avatarUrl: dto.User.AvatarURL,
            inviteCode: dto.User.InviteCode,
            role: dto.User.Role,
            status: dto.User.Status,
            adminPermissions: dto.User.AdminPermissions ?? [],
          },
        }),

      updateProfile: (dto: UserMeDTO) =>
        set((state) => ({
          user: state.user
            ? {
                ...state.user,
                email: dto.email,
                nickname: dto.nickname,
                avatarUrl: dto.avatar_url,
                inviteCode: dto.invite_code,
                status: dto.status,
                // role is NOT overwritten — /me does not return it
              }
            : null,
        })),

      logout: () =>
        set({
          accessToken: null,
          expiresAt: null,
          user: null,
        }),
    }),
    {
      name: STORAGE_KEY,
      partialize: (state) => ({
        accessToken: state.accessToken,
        expiresAt: state.expiresAt,
        user: state.user,
      }),
    },
  ),
);

/** Non-hook accessor for api-client configuration. */
export function getAccessToken(): string | null {
  return useAuthStore.getState().accessToken;
}

/** Called by api-client on 401 response. */
export function handleUnauthorized(): void {
  useAuthStore.getState().logout();
}
